import { useEffect, useState } from "react";
import axios from "axios";
import { Download, X, Check } from "lucide-react";
import { toast } from "sonner";
import { API } from "@/lib/api";
import { getToken } from "@/lib/auth";

/**
 * Export button + column picker for admin tabs.
 *
 * Props:
 *   kind: "suppliers" | "searches" | "quotes"
 *   buildUrl: (cols: string|null) => string  — function returning the export URL
 *                                             with whatever filters the parent has applied
 */
export default function ExportButton({ kind, buildUrl }) {
  const [open, setOpen] = useState(false);
  const [meta, setMeta] = useState(null);
  const [busy, setBusy] = useState(false);
  const [selected, setSelected] = useState(null);
  const [activePreset, setActivePreset] = useState("Everything");

  useEffect(() => {
    if (!open || meta) return;
    axios.get(`${API}/admin/export/columns`, {
      headers: { Authorization: `Bearer ${getToken()}` },
    }).then((r) => {
      setMeta(r.data);
      setSelected(Object.keys(r.data[kind] || {}));
    }).catch(() => toast.error("Could not load columns"));
  }, [open, meta, kind]);

  const applyPreset = (presetName) => {
    setActivePreset(presetName);
    setSelected(meta.presets[kind][presetName]);
  };

  const toggle = (col) => {
    setActivePreset("Custom");
    setSelected((s) => s.includes(col) ? s.filter(c => c !== col) : [...s, col]);
  };

  const download = async () => {
    if (!selected || selected.length === 0) {
      toast.error("Pick at least one column");
      return;
    }
    setBusy(true);
    try {
      const url = buildUrl(selected.join(","));
      const res = await fetch(url, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      if (!res.ok) throw new Error("Export failed");
      const blob = await res.blob();
      const a = document.createElement("a");
      const dlUrl = URL.createObjectURL(blob);
      a.href = dlUrl;
      const cd = res.headers.get("content-disposition") || "";
      const m = cd.match(/filename="?([^"]+)"?/);
      a.download = m ? m[1] : `spannr-${kind}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(dlUrl);
      toast.success("CSV downloaded");
      setOpen(false);
    } catch {
      toast.error("Export failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        data-testid={`export-button-${kind}`}
        className="text-[10px] uppercase tracking-wider font-bold flex items-center gap-1.5 px-3 h-9 border border-neutral-200 hover:border-[#FF5100] hover:text-[#FF5100] transition-colors"
      >
        <Download size={12} /> Export CSV
      </button>

      {open && (
        <div
          className="fixed inset-0 z-50 bg-black/40 grid place-items-center p-4"
          onClick={() => !busy && setOpen(false)}
          data-testid="export-modal"
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="bg-white border border-neutral-200 max-w-lg w-full max-h-[90vh] flex flex-col"
          >
            <div className="px-5 py-3 border-b border-neutral-200 flex items-center justify-between">
              <div>
                <div className="text-xs font-mono-tech tracking-[0.25em] text-[#FF5100]">EXPORT · {kind.toUpperCase()}</div>
                <div className="font-display text-xl font-extrabold leading-none mt-0.5">Pick columns</div>
              </div>
              <button onClick={() => setOpen(false)} className="p-1.5 text-neutral-500 hover:text-neutral-900">
                <X size={18} />
              </button>
            </div>

            {!meta ? (
              <div className="p-6 text-sm text-neutral-500 font-mono-tech">[ Loading... ]</div>
            ) : (
              <>
                <div className="px-5 py-3 border-b border-neutral-100">
                  <div className="text-[10px] uppercase tracking-[0.2em] font-mono-tech text-neutral-500 mb-2">Quick presets</div>
                  <div className="flex flex-wrap gap-1.5">
                    {Object.keys(meta.presets[kind] || {}).map((name) => (
                      <button
                        key={name}
                        onClick={() => applyPreset(name)}
                        data-testid={`export-preset-${name}`}
                        className={`text-[11px] uppercase tracking-wider font-bold px-2.5 h-8 border transition-colors ${
                          activePreset === name
                            ? "bg-[#FF5100] border-[#FF5100] text-white"
                            : "border-neutral-200 hover:border-neutral-900"
                        }`}
                      >
                        {name}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="px-5 py-3 overflow-y-auto flex-1">
                  <div className="text-[10px] uppercase tracking-[0.2em] font-mono-tech text-neutral-500 mb-2">
                    Columns ({selected?.length || 0} selected)
                  </div>
                  <div className="grid grid-cols-2 gap-1">
                    {Object.entries(meta[kind] || {}).map(([key, label]) => {
                      const on = selected?.includes(key);
                      return (
                        <button
                          key={key}
                          onClick={() => toggle(key)}
                          data-testid={`export-col-${key}`}
                          className={`flex items-center gap-2 px-2 py-1.5 text-sm text-left transition-colors ${
                            on ? "bg-[#FF5100]/10 text-neutral-900" : "text-neutral-600 hover:bg-neutral-50"
                          }`}
                        >
                          <span className={`w-4 h-4 border grid place-items-center shrink-0 ${
                            on ? "bg-[#FF5100] border-[#FF5100]" : "border-neutral-300"
                          }`}>
                            {on && <Check size={10} className="text-white" />}
                          </span>
                          <span className="truncate">{label}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div className="px-5 py-3 border-t border-neutral-200 flex items-center justify-between gap-2">
                  <div className="text-[11px] text-neutral-500">
                    Opens in Google Sheets via File → Import
                  </div>
                  <button
                    onClick={download}
                    disabled={busy}
                    data-testid="export-download"
                    className="px-4 h-10 bg-[#FF5100] hover:bg-[#E64A00] disabled:opacity-50 text-white text-xs font-bold uppercase tracking-wider inline-flex items-center gap-1.5"
                  >
                    <Download size={12} /> {busy ? "Exporting..." : "Download CSV"}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
}
